import React, { useState, useEffect, useRef } from 'react';
import { LuX } from 'react-icons/lu';
import useSchoolStore from '../../store/schoolStore';
import LoadingSpinner from '../common/LoadingSpinner';
import Button from '../common/Button';
import { Input, Select } from '../common/FormControls';

const QuickCommandDialog = ({
  isOpen,
  onClose,
  command,
  onSubmit
}) => {
  const { getAcademicYears, schoolData } = useSchoolStore();
  
  const [formValues, setFormValues] = useState({});
  const [academicYears, setAcademicYears] = useState([]);
  const [isLoadingAcademicYears, setIsLoadingAcademicYears] = useState(false);
  const [errors, setErrors] = useState({});
  const firstInputRef = useRef(null);

  // Initialize form values when dialog opens
  useEffect(() => {
    if (isOpen && command) {
      // Create simple object with parameter values
      const initialValues = {};
      command.parameters.forEach((param, index) => {
        initialValues[index] = param.value || '';
      });
      setFormValues(initialValues);
      setErrors({});
      
      // Load academic years if any parameter requires it
      const hasAcademicYearParam = command.parameters.some(p => p.type === 'academicYear');
      if (hasAcademicYearParam && academicYears.length === 0) {
        if(schoolData?.currentAcademicYear){
          const parameterIndex = command.parameters.findIndex(p => p.type === 'academicYear');
          if(parameterIndex>=0){
            setFormValues((prev)=>({...prev, [parameterIndex]: schoolData.currentAcademicYear}));
          }
        }
        loadAcademicYears();
      }
      
      // Focus the first input after a short delay to ensure the dialog is rendered
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen, command]);

  const loadAcademicYears = async () => {
    setIsLoadingAcademicYears(true);
    try {
      const result = await getAcademicYears();
      if (result.success) {
        setAcademicYears(result.value || []);
      }
    } catch (error) {
      console.error('Failed to load academic years:', error);
    } finally {
      setIsLoadingAcademicYears(false);
    }
  };

  const handleParameterChange = (paramIndex, value) => {
    // Simple, efficient state update
    setFormValues(prev => ({
      ...prev,
      [paramIndex]: value
    }));
    
    // Clear error for this parameter
    if (errors[paramIndex]) {
      const newErrors = { ...errors };
      delete newErrors[paramIndex];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    if (!command) return false;
    
    const newErrors = {};
    command.parameters.forEach((param, index) => {
      const value = formValues[index] || '';
      
      // Check if required field is empty
      if (param.required && !value.trim()) {
        newErrors[index] = 'This field is required';
        return;
      }
      
      // Type-specific validation
      if (value && param.type === 'num' && isNaN(value)) {
        newErrors[index] = 'Must be a valid number';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to check if form is valid without setting errors
  const isFormValid = () => {
    if (!command) return false;
    
    return command.parameters.every((param, index) => {
      const value = formValues[index] || '';
      
      // Check required fields
      if (param.required && !value.trim()) {
        return false;
      }
      
      // Type validation
      if (value && param.type === 'num' && isNaN(value)) {
        return false;
      }
      
      return true;
    });
  };

  const handleSubmit = () => {
    if (!command || !validateForm()) return;
    
    // Generate command string directly without mutating any objects
    let finalCommand = command.command;
    command.parameters.forEach((param, index) => {
      const value = formValues[index] || '';
      const placeholder = `{${index}}`;
      
      // If parameter is not required and value is empty, remove the placeholder completely
      if (!param.required && !value.trim()) {
        finalCommand = finalCommand.replace(placeholder, '');
      } else {
        finalCommand = finalCommand.replace(placeholder, value);
      }
    });
    
    onSubmit(finalCommand);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const renderParameterInput = (parameter, index) => {
    const hasError = errors[index];
    const controlClass = hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : '';

    const label = (
      <span className="text-[13px] font-medium text-ui-ink-2">
        {parameter.displayName}
        {parameter.required && <span className="ml-0.5 text-red-600">*</span>}
      </span>
    );

    const errorLine = hasError && <span className="text-xs text-red-600 dark:text-red-400">{errors[index]}</span>;

    if (parameter.type === 'academicYear') {
      return (
        <label key={index} className="flex flex-col gap-1.5">
          {label}
          {isLoadingAcademicYears ? (
            <span className="flex h-9 items-center gap-2 text-sm text-ui-ink-2">
              <LoadingSpinner size="small" />
              Loading academic years…
            </span>
          ) : (
            <Select
              ref={index === 0 ? firstInputRef : null}
              value={formValues[index] || ''}
              onChange={(event) => handleParameterChange(index, event.target.value)}
              className={controlClass}
            >
              <option value="">Select academic year</option>
              {academicYears.map((year) => (
                <option key={year.id || year} value={year.name || year}>
                  {year.name || year}
                </option>
              ))}
            </Select>
          )}
          {errorLine}
        </label>
      );
    }

    return (
      <label key={index} className="flex flex-col gap-1.5">
        {label}
        <Input
          ref={index === 0 ? firstInputRef : null}
          type={parameter.type === 'num' ? 'number' : 'text'}
          value={formValues[index] || ''}
          placeholder={parameter.placeholder}
          onChange={(event) => handleParameterChange(index, event.target.value)}
          onKeyDown={handleKeyDown}
          className={controlClass}
        />
        {errorLine}
      </label>
    );
  };

  if (!isOpen || !command) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="quick-command-title">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative flex max-h-[85vh] w-full max-w-lg animate-enter flex-col rounded-xl border border-ui-line bg-ui-card shadow-pop">
        <div className="flex items-start justify-between gap-4 border-b border-ui-line-soft px-6 py-4">
          <div className="min-w-0">
            <h2 id="quick-command-title" className="text-base font-semibold text-ui-ink">
              {command.title}
            </h2>
            <p className="mt-0.5 text-sm text-ui-ink-2">{command.description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 flex size-8 shrink-0 items-center justify-center rounded-lg text-ui-ink-3 transition-colors hover:bg-ui-hover hover:text-ui-ink"
          >
            <LuX aria-hidden className="size-4" />
          </button>
        </div>

        <div className="ui-scroll flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
          {command.parameters.map((parameter, index) => renderParameterInput(parameter, index))}
        </div>

        <div className="flex justify-end gap-2 border-t border-ui-line-soft px-6 py-4">
          <Button onClick={onClose} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="primary" disabled={!command || !isFormValid()}>
            {command.submitButtonText === 'Insert Command' ? 'Use this question' : command.submitButtonText}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickCommandDialog;
